/**
 * The signed-in person's own account: profile fields, preferences, notifications.
 *
 * Everything here is an endpoint the platform already ships and that the official
 * `frontend-app-account` consumes. Nothing is custom, nothing is proxied through a BFF.
 * Shapes and behaviours below were read off the live server, not from documentation —
 * where the platform contradicts its own docstrings the comment says so.
 *
 * ---------------------------------------------------------------------------------
 * THREE THINGS THAT COST A DAY IF YOU GUESS THEM.
 *
 * 1. THE PATCH MEDIA TYPE. `/api/user/v1/accounts/{username}` and
 *    `/api/user/v1/preferences/{username}` accept ONLY `application/merge-patch+json`.
 *    A plain `application/json` PATCH is refused outright — verified live:
 *
 *        PATCH … Content-Type: application/json
 *        415 {"detail": "Unsupported media type \"application/json\" in request."}
 *
 *    `openedx.patch` sends `application/json`, so every write in this file goes through
 *    `mergePatch` below rather than the shared client. That is the whole reason this
 *    file owns a request helper at all.
 *
 * 2. WHICH STORE OWNS WHICH FIELD. `time_zone` appears in the accounts payload AND in
 *    preferences. The accounts copy is always null and silently ignores writes — a PATCH
 *    of it returns 200 and changes nothing. Preferences is the one that works. The
 *    official MFE carries the same warning in `patchSettings`, and it is correct.
 *
 *    `account_privacy` is the mirror image: it is not a field on either serializer, but
 *    the accounts view forwards it to preferences for you (`_update_preferences_if_needed`),
 *    so writing it to the accounts endpoint DOES work and keeps name/bio/privacy in one
 *    round trip. That is what `frontend-app-profile` does, and it is what we do.
 *
 * 3. VISIBILITY IS SERVER-SIDE, NOT A DISPLAY RULE. Reading someone else's account
 *    returns only the fields their privacy setting exposes. Verified live: a `private`
 *    account read by another user returns exactly `account_privacy`, `profile_image`
 *    and `username` — bio, country and the rest are simply absent from the JSON, not
 *    blank. So a profile page renders what it was given; it never decides what to hide.
 *    Reading your OWN account always returns everything.
 * ---------------------------------------------------------------------------------
 */

import { ApiError, getCsrfToken, lmsOrigin, openedx, request } from './openedx';

/* ------------------------------------------------------------------- writes ----- */

/**
 * PATCH with the media type Open edX's account APIs demand. See note 1 in the header.
 *
 * Mirrors `request()` in `openedx.ts` — cookies, CSRF, and one retry when Django rotates
 * the token — but cannot reuse it, because that helper hardcodes `application/json` and
 * this API answers 415 to it.
 *
 * A 204 is a success with no body: preferences answers that way. Callers get the value
 * they sent back, since there is nothing else to return.
 */
async function mergePatch<T>(path: string, body: unknown, attempt = 0): Promise<T> {
  const res = await fetch(`${lmsOrigin}${path}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/merge-patch+json',
      'X-CSRFToken': await getCsrfToken(),
    },
    body: JSON.stringify(body),
  });

  if (res.ok) return res.status === 204 ? (undefined as T) : ((await res.json()) as T);

  // Django rotates the CSRF token with the session; re-fetch once and retry.
  if (res.status === 403 && attempt === 0) {
    await getCsrfToken(true);
    return mergePatch<T>(path, body, 1);
  }

  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    /* login redirects and proxy errors answer with HTML */
  }
  throw new ApiError(`PATCH ${path} failed`, res.status, payload);
}

/* ------------------------------------------------------------------ account ----- */

/** One entry of `social_links`. `platform` is a fixed vocabulary — see `SOCIAL_PLATFORMS`. */
export interface SocialLink {
  platform: string;
  social_link: string;
}

/** One entry of `language_proficiencies`. A bare ISO-639-1 code, e.g. `{ code: 'en' }`. */
export interface LanguageProficiency {
  code: string;
}

/**
 * The four rendered sizes of the profile photo, in pixels: 500 / 120 / 50 / 30.
 *
 * These are ABSOLUTE urls on the LMS host, and they are always present — when
 * `has_image` is false they point at the platform's stock silhouette. We ignore the
 * stock image and draw initials instead, so `has_image` is the field to branch on, not
 * the presence of a url.
 */
export interface ProfileImage {
  has_image: boolean;
  image_url_full: string;
  image_url_large: string;
  image_url_medium: string;
  image_url_small: string;
}

/**
 * `GET /api/user/v1/accounts/{username}`.
 *
 * Every field is optional because the server omits — rather than nulls — anything the
 * subject's privacy setting does not expose. See note 3 in the header. Reading your own
 * account fills all of them in.
 */
export interface Account {
  /** 'all_users' | 'private' | 'custom'. Derived, not stored — see `visibilityOf`. */
  account_privacy?: string;
  profile_image?: ProfileImage;
  username?: string;
  /** Full name as one string. Open edX has no first/last split — see `PROFILE_FIELDS`. */
  name?: string;
  email?: string;
  bio?: string | null;
  country?: string | null;
  /** US/CA sub-division code. Only meaningful when `country` is one of those two. */
  state?: string | null;
  language_proficiencies?: LanguageProficiency[];
  level_of_education?: string | null;
  gender?: string | null;
  year_of_birth?: number | null;
  goals?: string | null;
  mailing_address?: string | null;
  social_links?: SocialLink[];
  date_joined?: string;
  is_active?: boolean;
  /**
   * Always null and unwritable on this endpoint. The real one lives in preferences.
   * Kept in the type only so nobody re-adds it to a form after seeing it in the payload.
   */
  time_zone?: null;
  /**
   * True when the platform believes the account holder is under 13, which it infers
   * from a MISSING `year_of_birth` as readily as from a recent one. It forces privacy to
   * 'private' no matter what the preference says, and blocks the photo upload. A brand
   * new account has it set, which is why a fresh profile looks locked down for no
   * apparent reason — setting a year of birth clears it.
   */
  requires_parental_consent?: boolean;
  /** Earned certificates, or null when there are none / they are not visible. */
  course_certificates?: CourseCertificate[] | null;
  /** Pending full-name change awaiting ID verification, when that feature is on. */
  pending_name_change?: string | null;
}

/** Element of `course_certificates`. Only present for certificates already issued. */
export interface CourseCertificate {
  course_id?: string;
  course_display_name?: string;
  certificate_type?: string;
  created_date?: string;
  modified_date?: string;
  is_passing?: boolean;
  download_url?: string | null;
}

export const getAccount = (username: string) =>
  openedx.get<Account>(`/api/user/v1/accounts/${encodeURIComponent(username)}`);

/**
 * Save account fields. Returns the full updated account, which the server echoes.
 *
 * MERGE semantics: only the keys you send are touched. Sending `{ bio: '' }` clears the
 * bio; omitting `bio` leaves it alone. That is what makes it safe to save one card of a
 * settings page without shipping the whole form.
 *
 * `email` is accepted here but does NOT change on save — see `requestEmailChange`.
 */
export const patchAccount = (username: string, patch: Partial<Account>) =>
  mergePatch<Account>(`/api/user/v1/accounts/${encodeURIComponent(username)}`, patch);

/**
 * Fields this app is willing to write, and what the platform does with each.
 *
 * The accounts serializer accepts `city` as well, and it saves — but `city` is not in
 * `ACCOUNT_VISIBILITY_CONFIGURATION.admin_fields`, so it is never returned by a read,
 * even to the account holder. A control whose value can be written and never shown is a
 * control that looks broken, so it is deliberately absent.
 *
 * `username` and `date_joined` are rejected with a 400 naming the field:
 * "The 'username' field cannot be edited."
 */
export const PROFILE_FIELDS = [
  'name',
  'bio',
  'country',
  'state',
  'level_of_education',
  'gender',
  'year_of_birth',
  'language_proficiencies',
  'social_links',
  'goals',
  'mailing_address',
] as const;

/**
 * The three social platforms the serializer recognises.
 *
 * `platform` values are fixed by the server: anything else is rejected. Note `xTwitter`
 * — the id kept its historical spelling when the product was renamed, so the obvious
 * guesses ('x', 'twitter') both fail validation.
 *
 * The list is REPLACED wholesale on write, not merged, so a save has to send every link
 * the account should keep. `setSocialLink` below does that bookkeeping.
 */
export const SOCIAL_PLATFORMS: { id: string; label: string; placeholder: string }[] = [
  { id: 'linkedin', label: 'LinkedIn', placeholder: 'https://www.linkedin.com/in/…' },
  { id: 'xTwitter', label: 'X', placeholder: 'https://x.com/…' },
  { id: 'facebook', label: 'Facebook', placeholder: 'https://www.facebook.com/…' },
];

export function socialLinkFor(account: Account | undefined, platform: string): string {
  return account?.social_links?.find((link) => link.platform === platform)?.social_link ?? '';
}

/** Build the complete replacement list after editing one platform's url. */
export function setSocialLink(current: SocialLink[], platform: string, url: string): SocialLink[] {
  const others = current.filter((link) => link.platform !== platform);
  const trimmed = url.trim();
  return trimmed ? [...others, { platform, social_link: trimmed }] : others;
}

/* -------------------------------------------------------------- preferences ----- */

/**
 * `GET /api/user/v1/preferences/{username}` — a flat string map, `{}` when nothing is set.
 *
 * Note the KEY SPELLING. The interface-language key is `pref-lang` with a HYPHEN, which
 * is why the official MFE runs its payload through `convertKeyNames({ pref_lang: 'pref-lang' })`
 * after snake-casing it. A `pref_lang` key is accepted and stored as a preference nobody
 * reads, so the language silently never changes.
 */
export type Preferences = Record<string, string>;

export const getPreferences = (username: string) =>
  openedx.get<Preferences>(`/api/user/v1/preferences/${encodeURIComponent(username)}`);

/**
 * Save preferences. Answers 204 with no body, so the sent values are echoed back.
 *
 * Sending `null` for a key deletes it — merge-patch semantics again.
 */
export async function patchPreferences(username: string, patch: Preferences): Promise<Preferences> {
  await mergePatch<void>(`/api/user/v1/preferences/${encodeURIComponent(username)}`, patch);
  return patch;
}

/** Preference key for the learner's time zone. Deadlines and dates are rendered in it. */
export const TIME_ZONE_KEY = 'time_zone';

/**
 * `GET /api/user/v1/preferences/time_zones/` — every zone the platform will accept.
 *
 * 433 entries live, each with a `description` the server has already formatted as
 * "Asia/Kuala Lumpur (+08, UTC+0800)". We render that string rather than computing an
 * offset ourselves, because the server's is the one it will validate against.
 *
 * There is a second copy of this endpoint at `/user_api/v1/preferences/time_zones/`.
 * Use THIS one: the app only proxies `/api`, so the `/user_api` spelling never reaches
 * the LMS in development and returns the SPA's own index.html instead.
 */
export interface TimeZone {
  time_zone: string;
  description: string;
}

export const getTimeZones = () =>
  openedx.get<TimeZone[]>('/api/user/v1/preferences/time_zones/');

/* --------------------------------------------------------- field vocabularies --- */

/**
 * The platform's own option lists for the demographic fields.
 *
 * Taken from the registration form descriptor, which is how `frontend-app-account`
 * sources its country list too — the alternative is hardcoding vocabularies that then
 * drift from what the server will accept.
 *
 * WHICH fields appear is per-install: the descriptor only describes fields the operator
 * has switched on in `REGISTRATION_EXTRA_FIELDS`. On this backend that is
 * `year_of_birth`, `level_of_education`, `gender`, `goals` and `mailing_address` —
 * `country` is NOT among them, so no country vocabulary is available and the control
 * that would need it hides itself. That is the honest behaviour: the field is writable,
 * but we have no server-sanctioned list of values to offer, and inventing one would put
 * codes in front of people that the server may reject.
 */
export interface RegistrationField {
  name: string;
  type: string;
  label?: string;
  options?: { value: string; name: string }[];
}

interface RegistrationForm {
  fields: RegistrationField[];
}

export async function getFieldOptions(): Promise<Record<string, [string, string][]>> {
  const form = await openedx.get<RegistrationForm>('/api/user/v1/account/registration/');
  const out: Record<string, [string, string][]> = {};
  for (const field of form.fields ?? []) {
    if (field.options?.length) {
      out[field.name] = field.options.map((option) => [option.value, option.name]);
    }
  }
  return out;
}

/* ------------------------------------------------------------------ privacy ----- */

export const VISIBILITY_ALL = 'all_users';
export const VISIBILITY_PRIVATE = 'private';

/**
 * What other learners can see, and why it might not be what was chosen.
 *
 * `requires_parental_consent` overrides the preference unconditionally — the serializer
 * returns 'private' before it ever looks at the setting. Without this distinction the
 * settings page shows a visibility control that appears not to save, when in fact the
 * save worked and something else is winning.
 */
export function visibilityOf(account: Account | undefined): {
  value: string;
  isPublic: boolean;
  forced: boolean;
} {
  const value = account?.account_privacy ?? VISIBILITY_PRIVATE;
  return {
    value,
    isPublic: value === VISIBILITY_ALL,
    forced: account?.requires_parental_consent === true,
  };
}

/**
 * Fields a public profile actually shares, from `ACCOUNT_VISIBILITY_CONFIGURATION`.
 *
 * Listed so the privacy control can say what "public" means in specifics rather than
 * asking people to trust a switch. `name` is NOT here on purpose: it is only shared
 * under the 'custom' setting, so a public profile shows the username, not the real name.
 */
export const PUBLIC_PROFILE_FIELDS =
  'your photo, username, about-me, country, languages, education, links and the date you joined';

/* ------------------------------------------------------------ profile photo ----- */

/**
 * `POST|DELETE /api/user/v1/accounts/{username}/image`.
 *
 * Multipart with a part named `file`, so it cannot go through the shared JSON client —
 * and must NOT set Content-Type by hand, or the browser omits the multipart boundary and
 * the server rejects the body it cannot parse.
 *
 * Limits are the server's, read from its settings rather than assumed: 100 bytes to
 * 1 MiB, and JPEG, PNG or GIF only. (The design prototype says 2 MB; the server says
 * 1 MiB, and the server is the one that refuses.) Both endpoints answer 204.
 *
 * Refused outright while `requires_parental_consent` is set.
 */
export const IMAGE_MAX_BYTES = 1024 * 1024;
export const IMAGE_MIN_BYTES = 100;
export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif'];
export const IMAGE_RULES = 'JPG, PNG or GIF · up to 1 MB · square looks best';

/** Client-side check, so an obviously wrong file fails instantly instead of over the wire. */
export function checkImage(file: File): string | null {
  if (!IMAGE_TYPES.includes(file.type)) {
    return 'That file is not a JPG, PNG or GIF. Pick an image in one of those formats.';
  }
  if (file.size > IMAGE_MAX_BYTES) {
    return `That image is ${Math.ceil(file.size / 1024)} KB. The limit is 1 MB — export it smaller and try again.`;
  }
  if (file.size < IMAGE_MIN_BYTES) return 'That file is too small to be an image.';
  return null;
}

async function imageRequest(username: string, init: RequestInit, attempt = 0): Promise<void> {
  const res = await fetch(
    `${lmsOrigin}/api/user/v1/accounts/${encodeURIComponent(username)}/image`,
    { credentials: 'include', headers: { 'X-CSRFToken': await getCsrfToken() }, ...init },
  );
  if (res.ok) return;
  if (res.status === 403 && attempt === 0) {
    await getCsrfToken(true);
    return imageRequest(username, init, 1);
  }
  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    /* the image endpoints answer 204 with no body on success and JSON on failure */
  }
  throw new ApiError(`${init.method} profile image failed`, res.status, payload);
}

export function uploadProfileImage(username: string, file: File): Promise<void> {
  const body = new FormData();
  body.append('file', file, file.name);
  return imageRequest(username, { method: 'POST', body });
}

export const removeProfileImage = (username: string) =>
  imageRequest(username, { method: 'DELETE' });

/* ---------------------------------------------------------------- password ------ */

/**
 * Where the platform says to post a password reset request.
 *
 * `GET /api/user/v1/account/password_reset/` is a FORM DESCRIPTOR, not the reset itself
 * — it answers 405 to a POST. What it returns is the platform describing its own form:
 *
 *     { "method": "post", "submit_url": "/account/password",
 *       "fields": [ { "name": "email", … } ] }
 *
 * So the submit url is discovered rather than hardcoded, and this app follows the server
 * if it ever moves. (`/password_reset/` is a second, older entry point to the same
 * flow — the one the official MFE still hardcodes.)
 */
interface PasswordResetForm {
  method?: string;
  submit_url?: string;
}

/**
 * Ask the platform to email a password reset link.
 *
 * THERE IS NO INSTANT PASSWORD CHANGE API. Open edX has no "current password + new
 * password" endpoint for a learner; the only route is this one, which mails a one-time
 * link. The settings page has to say that rather than presenting the three-box form the
 * design prototype drew, because that form cannot be honoured.
 *
 * NOTHING IS SENT. The handler prefers `request.user.email` over any posted `email` when
 * the caller is signed in, so an empty body reaches the right inbox — and this app never
 * puts an address on the wire to have it reflected back in a log.
 *
 * Rate limited server-side: a second request too soon answers 403 with a plain-text
 * explanation, which is a real state the UI has to show rather than an error.
 */
export class PasswordResetUnreachable extends Error {
  constructor() {
    super('The password service did not answer.');
    this.name = 'PasswordResetUnreachable';
  }
}

export async function requestPasswordReset(): Promise<void> {
  const form = await openedx.get<PasswordResetForm>('/api/user/v1/account/password_reset/');
  const submitUrl = form.submit_url || '/account/password';

  const res = await fetch(`${lmsOrigin}${submitUrl}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-CSRFToken': await getCsrfToken(),
    },
    // Deliberately empty: see the note above.
    body: '',
  });

  const text = await res.text();

  // DID THIS EVEN REACH THE PLATFORM?
  //
  // `submit_url` is the one endpoint in this file that does NOT sit under `/api`, and in
  // development the dev server only forwards a fixed list of prefixes to the LMS. A
  // request for a path outside that list is answered by the SPA's own server instead —
  // either with index.html or with a 404 — and neither is a real answer from the reset
  // handler.
  //
  // Both are detectable, and detecting them is the difference between telling someone an
  // email is on its way and telling them the truth. A 404 is conclusive: the platform's
  // handler answers 200, 400, 403 or 405 and never 404, so a 404 here can only be our
  // own origin refusing a path it does not know about.
  const isHtml = text.includes('<html') || text.toLowerCase().includes('<!doctype');
  if (isHtml || res.status === 404) throw new PasswordResetUnreachable();

  if (res.status === 403) {
    throw new ApiError(
      'A reset link was requested very recently. Wait a few moments and try again.',
      403,
      null,
    );
  }
  if (!res.ok) throw new ApiError('Password reset request failed', res.status, text || null);
}

/* ------------------------------------------------------------ email change ------ */

/**
 * Changing the email address is a REQUEST, not a write.
 *
 * PATCHing `email` answers 200 and echoes the OLD address, because the server files a
 * pending change and mails a confirmation link to the NEW one; the account only moves
 * when that link is followed. Verified live — the address in the response after a
 * successful PATCH is unchanged.
 *
 * A settings page that shows the new value optimistically is therefore lying. This
 * returns void so a caller cannot mistake the echo for a result.
 */
export async function requestEmailChange(username: string, email: string): Promise<void> {
  await patchAccount(username, { email });
}

/* ------------------------------------------------------- notification prefs ----- */

/**
 * `GET|PUT /api/notifications/v3/configurations/` — account-wide notification settings.
 *
 * The v3 endpoint is the account-level one and needs no course id; v2 and earlier were
 * per-course. There is no `/api/notifications/configurations/` without the version —
 * that spelling 404s.
 *
 * Structure is three levels: APP ('discussion', 'updates', 'grading') → TYPE
 * ('course_updates', 'ora_reminder', …) → CHANNEL ('web', 'email', 'push'). The server
 * decides which of those exist, so the UI is generated from the response rather than
 * from a list of our own — that way an operator enabling a new notification app gets the
 * controls for free and we never render a toggle the backend has retired.
 *
 * `non_editable` names channels that must be shown as fixed. Almost every type lists
 * 'push' there on a web-only install, so honouring it is what stops the page offering
 * five push switches that cannot move.
 */
export interface NotificationType {
  web: boolean;
  push: boolean;
  email: boolean;
  email_cadence: string;
  /** Sentence written for a learner. Empty for most types; shown as-is when present. */
  info: string;
}

export interface NotificationApp {
  notification_types: Record<string, NotificationType>;
  enabled: boolean;
  /** type -> channels that cannot be changed on this install. */
  non_editable: Record<string, string[]>;
}

export interface NotificationPreferences {
  status: string;
  show_preferences: boolean;
  show_email_preferences: boolean;
  data: Record<string, NotificationApp>;
}

export type NotificationChannel = 'web' | 'email' | 'push';

export const getNotificationPreferences = () =>
  openedx.get<NotificationPreferences>('/api/notifications/v3/configurations/');

/**
 * Flip one channel of one notification type.
 *
 * PUT, not PATCH — and one toggle per request, which is the API's own granularity; there
 * is no bulk form. The response describes only the switch that moved, so the caller
 * refetches rather than trying to splice it into the tree.
 *
 * Goes through `request` rather than the `openedx` shorthand only because that object
 * has no `put` — the method, the credentials and the CSRF handling are identical.
 */
export function setNotificationChannel(
  app: string,
  type: string,
  channel: NotificationChannel,
  value: boolean,
): Promise<unknown> {
  return request<unknown>('/api/notifications/v3/configurations/', {
    method: 'PUT',
    json: {
      notification_app: app,
      notification_type: type,
      notification_channel: channel,
      value,
    },
  });
}

/** Turn `new_instructor_all_learners_post` into something readable. */
export function humanise(key: string): string {
  const words = key.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/* ---------------------------------------------------------- account closure ----- */

/**
 * `POST /api/user/v1/accounts/deactivate_logout/`, form-encoded, with the password.
 *
 * This is not a pause. It replaces the password with Django's unusable marker, logs the
 * session out, and queues the account for the platform's retirement pipeline, which
 * scrubs the personal data. Answers 204 on success, 401 when the password is wrong.
 *
 * The design prototype also drew "export your data" and a reversible "deactivate".
 * Neither exists as a learner-callable endpoint — retirement is driven by operator-only
 * APIs — so neither is built.
 */
export async function closeAccount(password: string): Promise<void> {
  await openedx.postForm('/api/user/v1/accounts/deactivate_logout/', { password });
}

/* ---------------------------------------------------------------- helpers ------- */

/** Name to print, falling back through the fields that are actually populated. */
export function displayName(account: Account | undefined): string {
  return account?.name?.trim() || account?.username || '';
}

/** Up to two initials for the avatar, from whichever name we have. */
export function initialsOf(account: Account | undefined): string {
  const source = displayName(account);
  const parts = source.split(/\s+/).filter(Boolean);
  const first = parts.at(0)?.charAt(0) ?? '';
  const last = parts.length > 1 ? (parts.at(-1)?.charAt(0) ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

/**
 * The photo to draw, or null to draw initials.
 *
 * Null when the platform is serving its stock silhouette: initials carry more
 * information than a grey outline, and every account without a photo would otherwise
 * look identical.
 */
export function avatarUrl(account: Account | undefined): string | null {
  const image = account?.profile_image;
  return image?.has_image ? image.image_url_full : null;
}

/**
 * Country code to country name, using the browser's own locale data.
 *
 * `Intl.DisplayNames` is the right tool here rather than a table of our own: it is
 * translated, it tracks ISO 3166 changes with the browser, and it means no list of
 * country names ships in this bundle. Unknown or unsupported codes fall back to the raw
 * code, which is still more use than nothing.
 */
function displayNameFor(type: 'region' | 'language', code: string | null | undefined): string {
  if (!code) return '';
  try {
    return new Intl.DisplayNames(undefined, { type }).of(code) ?? code;
  } catch {
    return code;
  }
}

export const countryName = (code: string | null | undefined) => displayNameFor('region', code);

export function languageNames(account: Account | undefined): string {
  const codes = account?.language_proficiencies ?? [];
  return codes.map((entry) => displayNameFor('language', entry.code)).filter(Boolean).join(', ');
}

/** "Joined March 2024" — month precision, because the day is nobody's business. */
export function joinedLabel(value: string | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

/**
 * Per-field validation messages the server sent back.
 *
 * The 400 body is `{"field_errors": {"bio": {"user_message": "…", "developer_message": "…"}}}`.
 * `user_message` is usually written for a learner and is used as-is — but not always:
 * an empty `name` yields the useless "This value is invalid." while `developer_message`
 * holds the real reason. So the two are compared and the specific one wins.
 */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError)) return {};
  const body = error.body as
    | { field_errors?: Record<string, { user_message?: string; developer_message?: string }> }
    | null;
  const out: Record<string, string> = {};
  for (const [field, detail] of Object.entries(body?.field_errors ?? {})) {
    const user = detail.user_message?.trim();
    if (user && user !== 'This value is invalid.') {
      out[field] = user;
    } else {
      // Pull the validator's own sentence out of "Value 'x' is not valid for field 'y': ['…']".
      const match = detail.developer_message?.match(/\['(.+?)'\]\s*$/);
      out[field] = match?.[1] ?? user ?? 'This value was not accepted.';
    }
  }
  return out;
}

/**
 * What to tell someone when a save fails, in terms of what happened and what to do.
 *
 * Never echoes the value that was rejected — these forms carry personal data and an
 * error string ends up in more places than the screen.
 */
export function explainSaveFailure(error: unknown, subject = 'change'): string {
  if (!(error instanceof ApiError)) {
    return `Your browser could not reach the academy, so the ${subject} was not saved. Check your connection and try again.`;
  }
  if (error.status === 401) {
    return `Your session has expired, so the ${subject} was not saved. Sign in again and it will go through.`;
  }
  if (error.status === 403) {
    return `You are not allowed to make that ${subject}. If you think that is wrong, contact the academy team.`;
  }
  if (error.status === 400) {
    const first = Object.values(fieldErrors(error))[0];
    return first ?? `The academy would not accept that ${subject}. Check the values and try again.`;
  }
  if (error.status === 404) {
    return `That account could not be found, so the ${subject} was not saved.`;
  }
  return `The academy could not save the ${subject} (error ${error.status}). Nothing has changed, so it is safe to try again.`;
}
